import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import { useQueryError } from './useQueryError';

// Cambio: Exportación nombrada en lugar de default
export function useCategories() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryError();

  const {
    data: categories = [],
    isLoading,
    error
  } = useQuery({
    queryKey: queryKeys.categories.all,
    queryFn: async () => {
      try {
        const { data, error: supabaseError } = await supabase
          .from("categories")
          .select("*")
          .order('id', { ascending: true });

        if (supabaseError) {
          throw supabaseError;
        }

        // Ensure "All" (id: 9) is at the beginning
        return data.sort((a, b) => {
          if (a.id === 9) return -1;
          if (b.id === 9) return 1;
          return a.name.localeCompare(b.name);
        });
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour - categories change infrequently
  });

  // Helper Functions
  const getCategorySlug = (categoryName) => {
    if (!categoryName) return '';
    return categoryName.toLowerCase().replace(/\s+/g, '_');
  };

  const getCategoryIdFromSlug = (slug) => {
    if (slug === 'all') return 9;
    const category = categories.find(
      cat => getCategorySlug(cat.name) === slug
    );
    return category ? category.id : 9;
  };

  const getCategoryNameById = (id) => {
    const category = categories.find(cat => cat.id === id);
    return category ? category.name : 'All';
  };

  const categoryExists = (slug) => {
    if (slug === 'all') return true;
    return categories.some(cat => getCategorySlug(cat.name) === slug);
  };

  // Prefetch handler for optimistic updates
  const prefetchCategory = async (categoryId) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.categories.byId(categoryId),
      queryFn: async () => {
        const { data, error } = await supabase
          .from("categories")
          .select("*")
          .eq("id", categoryId)
          .single();

        if (error) throw error;
        return data;
      }
    });
  };

  return {
    categories,
    isLoading,
    error,
    getCategorySlug,
    getCategoryIdFromSlug,
    getCategoryNameById,
    categoryExists,
    prefetchCategory
  };
}